import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
} from "@mui/material";
import { AppButton, AppTextField } from "../ui";
import {
  SUPPORTED_ELEMENT_FILE_HELPER_TEXT,
  SUPPORTED_ELEMENT_FILE_INPUT_ACCEPT,
  isSupportedElementFile,
} from "../../utility/elementFiles";
import "./UploadElementDialog.scss";

const UploadElementDialog = ({ open, onClose, onUpload }) => {
  const [newElementName, setNewElementName] = useState("");
  const [newElementDescription, setNewElementDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Handle drag and drop.
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      try {
        if (!isSupportedElementFile(file)) {
          throw new Error(
            `Unsupported file type. ${SUPPORTED_ELEMENT_FILE_HELPER_TEXT}`
          );
        }
        setSelectedFile(file);
      } catch (error) {
        console.error(error);
        alert(error.message);
      }
      e.dataTransfer.clearData();
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      try {
        if (!isSupportedElementFile(file)) {
          throw new Error(
            `Unsupported file type. ${SUPPORTED_ELEMENT_FILE_HELPER_TEXT}`
          );
        }
        setSelectedFile(file);
      } catch (error) {
        console.error(error);
        alert(error.message);
      }
    }
  };

  const handleClickDropzone = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUploadClick = async () => {
    if (loading || !selectedFile) {
      return;
    }

    setLoading(true);
    try {
      await onUpload({
        name: newElementName,
        description: newElementDescription,
        file: selectedFile,
      });
      // Optionally reset state after successful upload.
      setNewElementName("");
      setNewElementDescription("");
      setSelectedFile(null);
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: "upload-element-dialog__paper" }}
    >
      <DialogTitle className="upload-element-dialog__title">Upload New Element</DialogTitle>
      <DialogContent className="upload-element-dialog__content">
        <Grid container spacing={2} className="upload-element-dialog__form">
          <Grid item xs={12}>
            <AppTextField
              label="Element Name"
              value={newElementName}
              onChange={(e) => setNewElementName(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <AppTextField
              label="Element Description"
              value={newElementDescription}
              onChange={(e) => setNewElementDescription(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleClickDropzone}
              className="upload-element-dialog__dropzone"
            >
              {selectedFile ? (
                <Typography className="upload-element-dialog__dropzone-text">{selectedFile.name}</Typography>
              ) : (
                <Typography className="upload-element-dialog__dropzone-text">
                  Drag and drop a file here, or click to select a file
                </Typography>
              )}
              <input
                type="file"
                accept={SUPPORTED_ELEMENT_FILE_INPUT_ACCEPT}
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleFileInputChange}
              />
            </Box>
            <Typography variant="caption" className="upload-element-dialog__helper-text">
              {SUPPORTED_ELEMENT_FILE_HELPER_TEXT}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className="upload-element-dialog__actions">
        <AppButton
          kind="ghost"
          onClick={onClose}
          disabled={loading}
          className="upload-element-dialog__cancel"
        >
          Cancel
        </AppButton>
        <AppButton
          onClick={handleUploadClick}
          loading={loading}
          loadingLabel="Create & Upload"
          disabled={!selectedFile}
        >
          Create & Upload
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default UploadElementDialog;
